import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF, useTexture, useFBX } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import _ from "lodash";

// Import your animation utilities
import createAnimation from "../converter";
import blinkData from "../blendDataBlink.json";

const Avatar = ({
  avatar_url,
  isSpeaking,
  currentText,
  audioContext,
  audioSource,
}) => {
  const gltf = useGLTF(avatar_url);
  const mixer = useMemo(
    () => new THREE.AnimationMixer(gltf.scene),
    [gltf.scene]
  );

  // Refs for audio analysis and animation
  const analyserRef = useRef(null);
  const prevEnergyRef = useRef(0);
  const animationFrameRef = useRef(null);
  const currentVisemeActionRef = useRef(null);
  const lastAnalysisTimeRef = useRef(0);

  // Head and neck movement references
  const headNodeRef = useRef(null);
  const neckNodeRef = useRef(null);
  const spineNodeRef = useRef(null);

  // Enhanced blinking state with more frequent and varied blinking
  const blinkStateRef = useRef({
    nextBlink: Date.now() + Math.random() * 3000 + 1500, // 1.5-4.5 seconds for next blink (increased frequency)
    isBlinking: false,
    blinkProgress: 0,
    blinkSpeed: 1 + Math.random() * 0.3, // Slightly randomized base speed
    blinkHoldTime: 0.05 + Math.random() * 0.08, // Variable hold time (more natural)
    doubleBlinkChance: 0.18, // 18% chance of double blink (increased from 10%)
    tripleBlinkChance: 0.05, // 5% chance of triple blink (new)
    partialBlinkChance: 0.15, // 15% chance of partial blink (new)
    partialBlinkStrength: 0.7, // Maximum strength for partial blinks
    lastBlinkTime: 0, // Track when last blink occurred
    forcedBlinkCountdown: 7000, // Force a blink if none occurs for 7 seconds
  });

  // Enhanced head movement configuration with restricted vertical movement
  const headMovementRef = useRef({
    // Base rotations to return to
    baseRotationX: 0,
    baseRotationY: 0,
    baseRotationZ: 0,

    // Current and previous look targets for smooth interpolation
    currentLookTarget: new THREE.Vector3(0, 0.1, 0),
    previousLookTarget: new THREE.Vector3(0, 0.1, 0),

    // Transition state
    lookTransitionProgress: 1, // 1 means not transitioning
    lookTransitionDuration: 1.0, // Slightly faster transitions for more responsiveness

    // Micro-movement parameters with limited vertical movement
    microMovementAmplitude: {
      x: 0.012 + Math.random() * 0.008, // Normal horizontal amplitude
      y: 0.004 + Math.random() * 0.003, // Significantly reduced vertical amplitude
    },
    microMovementSpeed: {
      x: 0.6 + Math.random() * 0.4, // Normal horizontal speed
      y: 0.5 + Math.random() * 0.4, // Normal vertical speed (but with reduced amplitude)
    },
    microMovementPhase: {
      x: Math.random() * Math.PI * 2, // Random starting phases
      y: Math.random() * Math.PI * 2,
    },

    // More frequent major movements
    nextLookChange: Date.now() + Math.random() * 2500 + 1500,

    // Enhanced breathing animation
    breathingPhase: Math.random() * Math.PI * 2,
    breathingSpeed: 0.3 + Math.random() * 0.15,
    breathingAmplitude: 0.015 + Math.random() * 0.008,

    // Previous positions for inertia calculation
    prevHeadRotation: new THREE.Vector3(0, 0, 0),
    headVelocity: new THREE.Vector3(0, 0, 0),

    // Behavior parameters
    restlessness: Math.random() * 0.6 + 0.6,
    expressiveness: Math.random() * 0.4 + 0.9,

    // Keep horizontal movement but reduce vertical variation
    largeMovementChance: 0.15,
    verticalConstraint: true, // Flag to enforce vertical movement constraints
  });

  // Morph target references
  const morphTargetDictionaryRef = useRef({
    body: null,
    lowerTeeth: null,
  });

  // Load idle animation
  const idleFbx = useFBX("/idle15.fbx");
  const { animations: idleAnimations } = idleFbx;

  // Setup audio analyzer when audio context is available
  useEffect(() => {
    if (audioContext && audioSource && isSpeaking) {
      if (!analyserRef.current) {
        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.fftSize = 1024; // Reduced for faster processing
        analyserRef.current.smoothingTimeConstant = 0.3; // Slightly less smoothing for more responsiveness
      }

      audioSource.connect(analyserRef.current);
      analyserRef.current.connect(audioContext.destination);

      return () => {
        audioSource.disconnect(analyserRef.current);
        analyserRef.current.disconnect();
      };
    }
  }, [audioContext, audioSource, isSpeaking]);

  // Setup materials, morphs and find head node
  useEffect(() => {
    // Store initial rotation values once we find the nodes
    let headFound = false;
    let neckFound = false;
    let spineFound = false;

    gltf.scene.traverse((node) => {
      if (
        node.type === "Mesh" ||
        node.type === "LineSegments" ||
        node.type === "SkinnedMesh"
      ) {
        node.castShadow = true;
        node.receiveShadow = true;
        node.frustumCulled = false;

        if (node.name.includes("Wolf3D_Avatar")) {
          morphTargetDictionaryRef.current.body = node.morphTargetDictionary;
        }

        if (node.name.includes("TeethLower")) {
          morphTargetDictionaryRef.current.lowerTeeth =
            node.morphTargetDictionary;
        }
      }

      // Find bones for natural movement
      if (node.type === "Bone") {
        if (node.name.includes("Head") && !headFound) {
          headNodeRef.current = node;
          headMovementRef.current.baseRotationX = node.rotation.x;
          headMovementRef.current.baseRotationY = node.rotation.y;
          headMovementRef.current.baseRotationZ = node.rotation.z;

          // Initialize previous rotation for inertia calculations
          headMovementRef.current.prevHeadRotation.set(
            node.rotation.x,
            node.rotation.y,
            node.rotation.z
          );

          headFound = true;
        } else if (node.name.includes("Neck") && !neckFound) {
          neckNodeRef.current = node;
          neckFound = true;
        } else if (node.name.includes("Spine2") && !spineFound) {
          spineNodeRef.current = node;
          spineFound = true;
        }
      }
    });

    // Set randomized starting blink time
    blinkStateRef.current.nextBlink = Date.now() + Math.random() * 4000 + 2000;

    console.log(
      "Found bones - Head:",
      headFound,
      "Neck:",
      neckFound,
      "Spine:",
      spineFound
    );
  }, [gltf.scene]);

  // Generate visemes based on audio analysis
  const generateVisemes = () => {
    if (!analyserRef.current || !isSpeaking) return null;

    // Limit analysis frequency for better performance
    const now = Date.now();
    if (now - lastAnalysisTimeRef.current < 20) {
      // Process up to 50fps
      return null;
    }
    lastAnalysisTimeRef.current = now;

    // Get time domain data (waveform amplitude) for more responsive lip sync
    const dataArray = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Calculate RMS (root mean square) amplitude
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const amplitude = (dataArray[i] - 128) / 128;
      sum += amplitude * amplitude;
    }
    const rms = Math.sqrt(sum / dataArray.length);

    // Scale to a better range with faster response
    const scaledEnergy = Math.min(rms * 6, 1);

    // Add smoothing but with faster response
    const prevEnergy = prevEnergyRef.current || 0;
    const smoothedEnergy = prevEnergy * 0.4 + scaledEnergy * 0.6; // More weight on new values
    prevEnergyRef.current = smoothedEnergy;

    // Map energy to mouth shapes with more dynamic range
    return {
      mouthOpen: [smoothedEnergy * 0.6], // Increased range
      jawOpen: [smoothedEnergy * 0.3], // Increased range
      mouthSmileLeft: [0.2 + smoothedEnergy * 0.2], // Dynamic smile that increases with volume
      mouthSmileRight: [0.2 + smoothedEnergy * 0.2],
    };
  };

  // Enhanced natural blinking pattern with more varied behavior
  const updateBlinking = (deltaTime) => {
    const now = Date.now();
    const blinkState = blinkStateRef.current;

    // Force a blink if too much time has passed without blinking
    // (humans physically can't keep eyes open for too long)
    if (
      !blinkState.isBlinking &&
      now - blinkState.lastBlinkTime > blinkState.forcedBlinkCountdown
    ) {
      blinkState.isBlinking = true;
      blinkState.blinkProgress = 0;
      blinkState.blinkSpeed = 1.2 + Math.random() * 0.4; // Slightly faster for "relief" blink
    }
    // Normal blink timing
    else if (!blinkState.isBlinking && now > blinkState.nextBlink) {
      blinkState.isBlinking = true;
      blinkState.blinkProgress = 0;

      // Determine if this will be a partial blink
      const isPartialBlink = Math.random() < blinkState.partialBlinkChance;
      blinkState.partialBlinkAmount = isPartialBlink
        ? Math.random() * 0.3 + blinkState.partialBlinkStrength * 0.7 // 70-100% of partial strength
        : 1.0; // Full blink

      // Randomize blink speed - natural blinks have variable speeds
      // Fast blinks (~0.1s), medium blinks (~0.15s), slow blinks (~0.2s)
      const speedVariations = [0.8, 1.0, 1.2, 1.5];
      const speedIndex = Math.floor(Math.random() * speedVariations.length);
      blinkState.blinkSpeed = speedVariations[speedIndex] + Math.random() * 0.4;
    }

    // Process blinking animation
    if (blinkState.isBlinking) {
      blinkState.blinkProgress += deltaTime * 5 * blinkState.blinkSpeed; // Increased base speed

      // Apply the blink morph with natural asymmetry
      let blinkValueLeft = 0;
      let blinkValueRight = 0;

      // Humans rarely blink perfectly symmetrically
      // Add slight asymmetry between eyes (only for full blinks)
      const asymmetry =
        blinkState.partialBlinkAmount === 1.0 ? Math.random() * 0.1 - 0.05 : 0;

      if (blinkState.blinkProgress < 1) {
        // Closing eyes phase - eased for more natural movement
        // Use sine-based easing for more natural closing motion
        const closeEase = Math.sin(blinkState.blinkProgress * Math.PI * 0.5);
        blinkValueLeft = closeEase * blinkState.partialBlinkAmount;
        blinkValueRight = closeEase * blinkState.partialBlinkAmount + asymmetry;
      } else if (blinkState.blinkProgress < 1 + blinkState.blinkHoldTime) {
        // Hold closed phase (or partially closed for partial blinks)
        blinkValueLeft = blinkState.partialBlinkAmount;
        blinkValueRight = blinkState.partialBlinkAmount + asymmetry;
      } else if (blinkState.blinkProgress < 2 + blinkState.blinkHoldTime) {
        // Opening eyes phase - slower and eased
        const openProgress =
          blinkState.blinkProgress - 1 - blinkState.blinkHoldTime;
        // Use cosine-based easing for more natural opening (eyes open more quickly at first, then slow down)
        const openEase = 1 - Math.sin(openProgress * Math.PI * 0.5);
        blinkValueLeft = openEase * blinkState.partialBlinkAmount;
        blinkValueRight =
          openEase * blinkState.partialBlinkAmount +
          asymmetry * (1 - openProgress);
      } else {
        // Blink completed
        blinkState.isBlinking = false;
        blinkValueLeft = 0;
        blinkValueRight = 0;

        // Record when this blink finished
        blinkState.lastBlinkTime = now;

        // Schedule next blink with natural patterns
        // More frequent when speaking (due to facial exertion)
        // Different frequencies based on activities
        const activityFactor = isSpeaking ? 0.7 : 1.0; // Speaking causes ~30% more frequent blinks
        const baseBlinksPerMinute = 15; // Average adult blinks 15-20 times per minute
        const randomVariation = Math.random() * 0.5 + 0.75; // 75%-125% of base rate

        // Convert blinks-per-minute to milliseconds between blinks
        const msPerBlink =
          (60000 / baseBlinksPerMinute) * activityFactor * randomVariation;

        blinkState.nextBlink = now + msPerBlink;

        // Multi-blink patterns (very natural human behavior)
        // Double blinks, triple blinks, and rapid successions
        const multiBlinkRandom = Math.random();
        if (multiBlinkRandom < blinkState.doubleBlinkChance) {
          // Double-blink (quicker follow-up)
          blinkState.nextBlink = now + 150 + Math.random() * 150;
        } else if (
          multiBlinkRandom <
          blinkState.doubleBlinkChance + blinkState.tripleBlinkChance
        ) {
          // Triple-blink pattern setup (first of three)
          blinkState.nextBlink = now + 150 + Math.random() * 100;
          // The third will be scheduled after the second completes
        }
      }

      // Apply blink morphs to all relevant nodes
      gltf.scene.traverse((node) => {
        if (node.morphTargetInfluences && node.morphTargetDictionary) {
          if (typeof node.morphTargetDictionary.eyeBlinkLeft !== "undefined") {
            node.morphTargetInfluences[
              node.morphTargetDictionary.eyeBlinkLeft
            ] = blinkValueLeft;
          }
          if (typeof node.morphTargetDictionary.eyeBlinkRight !== "undefined") {
            node.morphTargetInfluences[
              node.morphTargetDictionary.eyeBlinkRight
            ] = blinkValueRight;
          }
        }
      });
    }
  };

  // Improved natural head movements
  const updateHeadMovements = (deltaTime, time) => {
    if (!headNodeRef.current) return;

    const headState = headMovementRef.current;
    const now = Date.now();

    // Major head movement changes with natural behavior patterns
    if (
      headState.lookTransitionProgress >= 1 &&
      now > headState.nextLookChange
    ) {
      // Save the previous look target for smooth transition
      headState.previousLookTarget.copy(headState.currentLookTarget);

      // Time to pick a new look target based on activity state
      const isListening = !isSpeaking;
      const expressiveness = headState.expressiveness; // Personal characteristic

      // Constrained target generation suitable for children's tutoring
      // Determine if this will be a larger, more dramatic movement (primarily horizontal)
      const isLargeMovement = Math.random() < headState.largeMovementChance;

      // Adjust target generation to favor horizontal movement and limit vertical
      let horizontalRange, verticalRange;

      if (isLargeMovement) {
        // Large movements primarily move horizontally
        horizontalRange = isListening
          ? 0.35 * expressiveness // Expressive horizontal movement
          : 0.25 * expressiveness;
        // Very limited vertical range even for large movements
        verticalRange = 0.05 * expressiveness;
      } else {
        // Normal movements
        horizontalRange = isListening
          ? 0.25 * expressiveness // Normal horizontal range
          : 0.15 * expressiveness;
        // Extremely limited vertical range for normal movements
        verticalRange = 0.035 * expressiveness;
      }

      // Generate a new target with natural distribution but constrained vertical movement
      const gaussianRandom = () => {
        return (
          ((Math.random() + Math.random() + Math.random() + Math.random()) / 4 -
            0.5) *
          2
        );
      };

      // Add occasional head tilts (very natural human behavior)
      const shouldTilt = Math.random() < 0.3;
      const tiltAmount = shouldTilt ? Math.random() * 0.1 - 0.05 : 0;

      // Generate target with possible emphasis in horizontal direction
      const directionalBias =
        Math.random() < 0.4 ? (Math.random() > 0.5 ? 0.5 : -0.5) : 0;
      const targetX =
        (gaussianRandom() + directionalBias * 0.3) * horizontalRange;

      // Generate vertical target but constrained to avoid excessive upward looking
      // For kids tutoring, we want to maintain more consistent eye level
      // Vertical movements should be subtle and limited
      let targetY;

      // Constrain vertical movement to favor looking straight or slightly down
      // This is more appropriate for a tutoring avatar for children
      if (headState.verticalConstraint) {
        // Intentionally biased distribution to favor level or slightly downward gaze
        // Appropriate for tutoring where looking at child's eye level is important
        const rawY = gaussianRandom();

        // Map the distribution to favor straight-ahead or slight downward movement
        // 0.1 is the baseline position (straight ahead)
        if (rawY > 0) {
          // Looking up - severely limit this (only allow tiny amounts)
          targetY = 0.1 + rawY * verticalRange * 0.2; // Only 20% of already limited range for upward
        } else {
          // Looking down - allow this within reasonable limits
          targetY = 0.1 + rawY * verticalRange * 0.8; // 80% of limited range for downward
        }
      } else {
        // Original behavior if constraint is disabled
        targetY = gaussianRandom() * verticalRange + 0.05;
      }

      // Sometimes look directly at viewer/camera - important for tutoring
      if (isListening && Math.random() < 0.5) {
        // 50% chance when listening (increased for tutoring)
        // When tutoring, consistent eye contact is valuable
        // Add minimal variance to "looking at camera" to maintain engagement but avoid robotic precision
        const cameraVariance = 0.015; // Reduced variance for more consistent engagement
        headState.currentLookTarget.set(
          Math.random() * cameraVariance * 2 - cameraVariance,
          0.1 + Math.random() * cameraVariance * 0.5 - cameraVariance * 0.3, // Favor level gaze
          0
        );
      } else {
        headState.currentLookTarget.set(targetX, targetY, tiltAmount);
      }

      // Reset transition with natural variability in speed
      headState.lookTransitionProgress = 0;

      // Transition duration varies based on how far we're moving
      // Larger movements take longer (more natural)
      const distance = headState.previousLookTarget.distanceTo(
        headState.currentLookTarget
      );
      headState.lookTransitionDuration =
        0.8 + distance * 2 + Math.random() * 0.5;

      // Schedule next major movement with natural variability
      // Based on restlessness characteristic and speaking/listening state
      const restlessnessFactor = headState.restlessness;
      const nextChangeDelay = isSpeaking
        ? 5000 - restlessnessFactor * 2000 + Math.random() * 3000 // 3-8 seconds when speaking
        : 3000 - restlessnessFactor * 1500 + Math.random() * 2000; // 1.5-5 seconds when listening

      headState.nextLookChange = now + nextChangeDelay;
    }

    // Progress any ongoing look transition
    if (headState.lookTransitionProgress < 1) {
      headState.lookTransitionProgress +=
        deltaTime / headState.lookTransitionDuration;
      headState.lookTransitionProgress = Math.min(
        headState.lookTransitionProgress,
        1
      );
    }

    // Custom easing functions for more natural movement
    // Combined ease-out and ease-in-out for realistic acceleration/deceleration
    const easeNatural = (t) => {
      // Start slow, accelerate, then decelerate at the end
      if (t < 0.5) {
        // Accelerating phase (ease-in-out for first half)
        return 0.5 * (1 - Math.cos(Math.PI * t));
      } else {
        // Decelerating phase (ease-out for second half)
        return 0.5 + 0.5 * Math.sin(((t - 0.5) * Math.PI) / 2);
      }
    };

    const t = easeNatural(headState.lookTransitionProgress);

    // Interpolate between previous and current look targets
    const interpolatedX =
      headState.previousLookTarget.x +
      (headState.currentLookTarget.x - headState.previousLookTarget.x) * t;
    const interpolatedY =
      headState.previousLookTarget.y +
      (headState.currentLookTarget.y - headState.previousLookTarget.y) * t;

    // Add multiple layers of micro-movements for liveliness
    // Primary micro-movement (slower, larger)
    const microX1 =
      Math.sin(
        time * headState.microMovementSpeed.x + headState.microMovementPhase.x
      ) * headState.microMovementAmplitude.x;
    const microY1 =
      Math.sin(
        time * headState.microMovementSpeed.y + headState.microMovementPhase.y
      ) * headState.microMovementAmplitude.y;

    // Secondary micro-movement (faster, smaller)
    const microX2 =
      Math.sin(time * 2.1 + headState.microMovementPhase.x * 1.3) *
      headState.microMovementAmplitude.x *
      0.3;
    const microY2 =
      Math.sin(time * 1.7 + headState.microMovementPhase.y * 1.7) *
      headState.microMovementAmplitude.y *
      0.3;

    // Add breathing movement (subtle up/down of chest and neck)
    const breathingMotion =
      Math.sin(time * headState.breathingSpeed + headState.breathingPhase) *
      headState.breathingAmplitude;

    // Combine all movements
    const finalX = interpolatedX + microX1 + microX2;
    const finalY = interpolatedY + microY1 + microY2;

    // Calculate velocity for inertia (smooths transitions and adds realism)
    const currentHeadPos = new THREE.Vector3(
      headState.baseRotationX + finalY, // Note: X rotation controls up/down
      headState.baseRotationY + finalX, // Y rotation controls left/right
      headState.baseRotationZ
    );

    // Calculate current velocity (smoothed)
    headState.headVelocity
      .subVectors(currentHeadPos, headState.prevHeadRotation)
      .multiplyScalar(1 / deltaTime) // Convert to velocity units
      .multiplyScalar(0.2); // Reduce influence for stability

    // Apply velocity with damping for more fluid motion
    const inertiaFactor = 0.92; // Damping factor (lower = more damping)
    const inertiaInfluence = 0.03; // How much inertia affects movement

    const inertiaX = headState.headVelocity.y * inertiaInfluence; // Y velocity affects X rotation
    const inertiaY = headState.headVelocity.x * inertiaInfluence; // X velocity affects Y rotation

    // Update previous position for next frame
    headState.prevHeadRotation.copy(currentHeadPos);

    // Adjusted coordinated movements with limited vertical motion
    // Extract any Z rotation (tilt) from the current look target
    const tiltZ = headState.currentLookTarget.z || 0;

    if (headNodeRef.current) {
      // Head has good horizontal movement but restricted vertical movement
      // Reduced vertical multiplier to minimize looking up behaviors
      headNodeRef.current.rotation.x =
        headState.baseRotationX + finalY * 0.8 + inertiaY;
      // Maintain expressive horizontal movement
      headNodeRef.current.rotation.y =
        headState.baseRotationY + finalX * 1.2 + inertiaX;
      // Subtle head tilt for natural feel
      headNodeRef.current.rotation.z =
        headState.baseRotationZ + tiltZ + microX1 * 0.2;
    }

    if (neckNodeRef.current) {
      // Neck movements follow head but with reduced vertical component
      neckNodeRef.current.rotation.x = finalY * 0.4 + breathingMotion;
      neckNodeRef.current.rotation.y = finalX * 0.7;
      // Very subtle tilt follow-through
      neckNodeRef.current.rotation.z = tiltZ * 0.3;
    }

    if (spineNodeRef.current) {
      // Spine maintains minimal movement
      spineNodeRef.current.rotation.x = breathingMotion; // Almost no vertical movement, just breathing
      spineNodeRef.current.rotation.y = finalX * 0.2; // Moderate follow-through on horizontal
      // Very minimal tilt
      spineNodeRef.current.rotation.z = tiltZ * 0.1;
    }
  };

  // Animation update loop
  useFrame((state, deltaTime) => {
    const time = state.clock.getElapsedTime();

    // Update natural head movements
    updateHeadMovements(deltaTime, time);

    // Update natural blinking
    updateBlinking(deltaTime);

    // Handle lip sync for speaking
    if (isSpeaking && analyserRef.current) {
      const visemeData = generateVisemes();
      if (visemeData && morphTargetDictionaryRef.current.body) {
        Object.entries(visemeData).forEach(([key, value]) => {
          const morphIndex = morphTargetDictionaryRef.current.body[key];
          if (typeof morphIndex !== "undefined") {
            gltf.scene.traverse((node) => {
              if (
                node.morphTargetInfluences &&
                node.morphTargetDictionary &&
                typeof node.morphTargetDictionary[key] !== "undefined"
              ) {
                node.morphTargetInfluences[node.morphTargetDictionary[key]] =
                  value[0];
              }
            });
          }
        });
      }
    } else if (!isSpeaking) {
      // Return to rest position when not speaking
      const restMorphs = {
        mouthOpen: 0.02,
        jawOpen: 0.01,
        mouthSmileLeft: 0.2,
        mouthSmileRight: 0.2,
      };

      if (morphTargetDictionaryRef.current.body) {
        Object.entries(restMorphs).forEach(([key, value]) => {
          const morphIndex = morphTargetDictionaryRef.current.body[key];
          if (typeof morphIndex !== "undefined") {
            gltf.scene.traverse((node) => {
              if (
                node.morphTargetInfluences &&
                node.morphTargetDictionary &&
                typeof node.morphTargetDictionary[key] !== "undefined"
              ) {
                // Smoothly transition to rest pose
                const current =
                  node.morphTargetInfluences[node.morphTargetDictionary[key]];
                node.morphTargetInfluences[node.morphTargetDictionary[key]] =
                  current + (value - current) * 0.1;
              }
            });
          }
        });
      }
    }

    mixer.update(1 / 60);
  });

  // Setup idle animation
  useEffect(() => {
    if (idleAnimations?.[0]) {
      const idleClip = idleAnimations[0].clone();
      idleClip.tracks = idleClip.tracks.filter((track) => {
        // Filter out head and neck tracks since we'll control them manually
        return (
          !track.name.includes("Head") &&
          !track.name.includes("Neck") &&
          track.name.includes("Spine")
        );
      });

      const action = mixer.clipAction(idleClip);
      action.play();

      return () => {
        action.stop();
      };
    }
  }, [mixer, idleAnimations]);

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
};

export default Avatar;
